import customFetch from "./helper"
import {
  EnrichedCollectRecord,
  Collectible,
  UnsavedCollectible,
  CompletedCollectible,
  PendingCollectible,
} from "@/types/collectibles"

import { ErrorResponse, isErrorResponse } from "@types"

const API = "/collectibles"

export type getCollectorsResponse = {
  total: number
  records: Array<EnrichedCollectRecord>
}

function isEmptyObject(obj: object): boolean {
  return Object.keys(obj).length === 0
}

/**
 * Undefined if not found
 */
export async function getPostCollectible(
  noteId: string
): Promise<Collectible | undefined> {
  const response = await customFetch<Collectible | Record<string, never>>(
    `${API}/notes/${noteId}/postcollectible`,
    undefined,
    {
      ignoreImpersonation: true,
      bypassCookies: true,
    }
  )

  if (isEmptyObject(response)) {
    return undefined
  }

  return response as Collectible
}

/**
 * Fetches numToFetch avatars of collectors.
 *
 * Note that this does NOT fetch the full collector records, just the avatars.
 *
 */
export async function getCollectorsOverview(
  collectibleId: string,
  numAvatarsToFetch?: number
): Promise<getCollectorsResponse> {
  const query = new URLSearchParams({
    size: numAvatarsToFetch?.toString() || "10",
  })

  const res = await customFetch<getCollectorsResponse>(
    `${API}/collectors/overview/${collectibleId}?${query.toString()}`,
    {},
    {
      bypassCookies: true,
    }
  )

  if (isErrorResponse(res)) {
    console.error("Error getting collectors", res)
    return { total: 0, records: [] }
  }

  return res
}

/**
 * Fetches numToFetch collectors. This fetches numToFetch collect records,
 * which include the collector's avatar, name, and other metadata.
 *
 * Also note that this will throw an exception when used for v0 (deprecated) collectibles!
 *
 */
export async function getCollectors(
  collectibleId: string,
  numToFetch: number,
  createdAtCursor?: number,
  blogIdContext?: string,
  sort?: "asc" | "desc"
): Promise<Array<EnrichedCollectRecord>> {
  // Construct query parameters based on provided values
  const params = new URLSearchParams({
    limit: numToFetch.toString(),
    ...(createdAtCursor && { createdAtCursor: createdAtCursor.toString() }),
    ...(blogIdContext && { blogIdContext }),
    ...(sort && { sort }),
  })

  const res = await customFetch<Array<EnrichedCollectRecord>>(
    `${API}/collectors/${collectibleId}?${params.toString()}`,
    {},
    {
      bypassCookies: true,
    }
  )

  if (isErrorResponse(res)) {
    console.error("Error getting collectors", res)
    throw new Error("Error getting collectors")
  }

  console.log("GOT ALL COLLECTORS", res)

  return res
}

/**
 * Creates a new collectible or updates an existing one.
 *
 * The corresponding backend call is idempotent.
 */
export async function addOrUpdateCollectible(
  body: UnsavedCollectible | CompletedCollectible | PendingCollectible
): Promise<{ success: boolean; id: string } | ErrorResponse> {
  try {
    const data = await customFetch<{
      success: boolean
      id: string
    }>(`${API}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    })

    return data
  } catch (error) {
    console.error("Error in addOrUpdateCollectible:", error)
    return {
      success: false,
      msg: "An error occurred while processing your request.",
    }
  }
}

/**
 * Retrieves total number of collects on blog.
 */
export async function getBlogCollectCount(blogSlug: string): Promise<number> {
  try {
    const data = await customFetch<{
      success: boolean
      collectCount: number
    }>(
      `${API}/blog/${decodeURIComponent(blogSlug)}/count`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        next: {
          revalidate: 60 * 60 * 12, // Cache for 12 hour
        },
      },
      {
        bypassCookies: true,
      }
    )

    return data.collectCount
  } catch (error) {
    console.error("Error in getBlogCollectCount:", error)
    return 0
  }
}
